import type { CatalogResponse, PriceModel } from "./types";
import { createModelSlug } from "../shared/routes";

const docs: Record<string, string> = {
  openai: "https://developers.openai.com/api/docs/pricing",
  anthropic: "https://platform.claude.com/docs/en/about-claude/pricing",
  google: "https://ai.google.dev/gemini-api/docs/pricing",
  xai: "https://docs.x.ai/developers/models",
  deepseek: "https://api-docs.deepseek.com/quick_start/pricing-details-usd",
  mistral: "https://docs.mistral.ai/inference/pricing",
};

const names: Record<string, string> = {
  openai: "OpenAI",
  anthropic: "Anthropic",
  google: "Google",
  xai: "xAI",
  deepseek: "DeepSeek",
  mistral: "Mistral",
};

function model(
  providerId: string,
  id: string,
  name: string,
  input: number,
  output: number,
  context: number,
  cacheRead?: number,
): PriceModel {
  return {
    uid: `${providerId}:${id}`,
    slug: createModelSlug(id, name),
    id,
    name,
    providerId,
    providerName: names[providerId],
    providerDoc: docs[providerId],
    logo: `https://models.dev/logos/${providerId}.svg`,
    cost: { input, output, cacheRead },
    context,
    capabilities: {
      reasoning: true,
      tools: true,
      structured: providerId !== "anthropic",
      vision: !["deepseek", "mistral"].includes(providerId),
      audio: false,
      openWeights: providerId === "deepseek" || providerId === "mistral",
    },
  };
}

const models = [
  model("openai", "gpt-6-astra", "GPT-6 Astra", 10, 50, 1_050_000, 1),
  model("openai", "gpt-5.6-sol", "GPT-5.6 Sol", 4, 20, 1_050_000, 0.4),
  model("openai", "gpt-5.6-terra", "GPT-5.6 Terra", 2, 12, 1_050_000, 0.2),
  model("openai", "gpt-5.6-luna", "GPT-5.6 Luna", 0.2, 1.2, 1_050_000, 0.02),
  model("anthropic", "claude-opus-5", "Claude Opus 5", 5, 25, 1_000_000, 0.5),
  model("anthropic", "claude-sonnet-5", "Claude Sonnet 5", 2, 10, 1_000_000, 0.2),
  model("anthropic", "claude-haiku-4-5", "Claude Haiku 4.5", 1, 5, 200_000, 0.1),
  model("google", "gemini-3.8-flash", "Gemini 3.8 Flash", 0.75, 3.75, 1_048_576, 0.075),
  model("google", "gemini-3.5-flash-lite", "Gemini 3.5 Flash-Lite", 0.3, 2.5, 1_048_576, 0.03),
  model("xai", "grok-4.6", "Grok 4.6", 2, 6, 500_000, 0.3),
  model("deepseek", "deepseek-chat", "DeepSeek Chat", 0.27, 1.1, 64_000, 0.07),
  model("deepseek", "deepseek-reasoner", "DeepSeek Reasoner", 0.55, 2.19, 64_000, 0.14),
  model("mistral", "mistral-large-latest", "Mistral Large 3", 0.5, 1.5, 256_000, 0.05),
  model("mistral", "mistral-small-latest", "Mistral Small 4", 0.15, 0.6, 256_000, 0.015),
];

export const fallbackCatalog: CatalogResponse = {
  models,
  providers: Object.keys(names).map((id) => ({
    id,
    name: names[id],
    count: models.filter((item) => item.providerId === id).length,
  })),
  updatedAt: "2026-09-07T00:00:00.000Z",
  source: "snapshot",
};
